import React, { useEffect, useRef, useState } from 'react';
import mermaid from 'mermaid';

mermaid.initialize({
  startOnLoad: false,
  theme: 'neutral',
  securityLevel: 'loose',
});

let _mermaidCounter = 0;

export default function MermaidBlock({ code, isStreaming = false }) {
  const containerRef = useRef(null);
  const [error, setError] = useState(null);

  function normaliseMermaidCode(code) {
    // LLMs sometimes emit mermaid with 2+ spaces instead of newlines.
    // Replace runs of 2+ spaces that separate diagram statements with actual newlines.
    // Guard: don't touch spaces inside quoted strings or single-space indentation.
    return code
      .replace(/[ \t]{2,}/g, '\n')   // collapse multi-space runs → newline
      .replace(/\n{3,}/g, '\n\n')    // collapse triple+ newlines
      .trim();
  }

  useEffect(() => {
    // Don't attempt render while streaming — code is incomplete
    if (isStreaming) return;
    if (!containerRef.current || !code?.trim()) return;

    setError(null);
    const normalisedCode = normaliseMermaidCode(code);
    const id = `mermaid-diagram-${++_mermaidCounter}`;   // fresh ID every render

    mermaid
      .render(id, normalisedCode)
      .then(({ svg }) => {
        if (containerRef.current) {
          containerRef.current.innerHTML = svg;
        }
      })
      .catch(() => {
        // Mermaid injects an error SVG into the DOM before rejecting —
        // clear the container and remove the detached element Mermaid
        // appends to <body> so nothing error-related stays visible.
        if (containerRef.current) {
          containerRef.current.innerHTML = '';
        }
        document.getElementById(id)?.remove();
        setError(true);
      });
  }, [code, isStreaming]);

  // Show a subtle placeholder while the diagram is still being streamed
  if (isStreaming || !code?.trim()) {
    return (
      <div className="mermaid-block mermaid-block--loading">
        <span className="mermaid-loading-label">Rendering diagram…</span>
      </div>
    );
  }

  // On error, fall back to showing the raw code block silently
  if (error) {
    return (
      <pre className="md-pre">
        <code>{code}</code>
      </pre>
    );
  }

  return (
    <div
      ref={containerRef}
      className="mermaid-block"
    />
  );
}